<script setup>
  import { ref, computed, defineAsyncComponent, onErrorCaptured, watch } from 'vue'
  import { useSheetContentComponent } from '@/composables/useSheetContentComponent';
  import { useSheetController } from "@/composables/useSheetsController";
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { useBugHandler } from '@/composables/useBugHandler';
  import SheetBase from "@/components/SheetBase.vue";
  import SheetContentBase from "@/components/SheetContentBase.vue";
  import LoaderCircle from '@/components/LoaderCircle.vue';
  import ErrorBlock from '@/components/ErrorBlock.vue';

  const props = defineProps({
    position: {
      type: String,
      default: 'primary'
    }
  });

  const type = 'config';
  const isDesktop = useMediaQuery('(min-width: 840px)');

  const { report } = useBugHandler();

  let error = ref(null);
  let retryKey = ref(0);

  useSheetController.registerSheet(type, props.position);

  const components = {
    SheetContentCustomization: defineAsyncComponent(() => import('@/components/SheetContentMapCustomization.vue')),
    SheetContentMapSelection: defineAsyncComponent(() => import('@/components/SheetContentMapSelection.vue')),
    SheetContentFavorites: defineAsyncComponent(() => import('@/components/SheetContentFavorites.vue')),
    SheetContentSettings: defineAsyncComponent(() => import('@/components/SheetContentMapSettings.vue')),
    SheetContentStormSelection: defineAsyncComponent(() => import('@/components/SheetContentStormSelection.vue')),
    SheetContentRegionalNavigation: defineAsyncComponent(() => import('@/components/SheetContentRegionalNavigation.vue')),
    SheetContentStormPrintMesonet: defineAsyncComponent(() => import('@/components/SheetContentStormPrintMesonet.vue')),
    SheetContentLayersDashboard: defineAsyncComponent(() => import('@/components/SheetContentLayersDashboard.vue')),
    SheetContentMapCustomLayers: defineAsyncComponent(() => import('@/components/SheetContentMapCustomLayers.vue')),
  };

  const isSheetOpen = computed(() => {
    return useSheetController.sheets[type].open;
  })

  const startingHeightState = computed(() => isDesktop.value ? 'maximized' : 'mid');

  const errorContent = computed(() => ({
    title: 'There was a problem loading this panel.',
    message: 'Please try again later.',
    errorText: error.value?.message
  }));

  function handleRetry() {
    error.value = null;
    retryKey.value++;
  }

  onErrorCaptured((e) => {
    error.value = e;
    console.warn(e);

    report(e, {
      component: 'SheetConfig',
      content: useSheetController.sheets[type].content
    });

    return false;
  })

  // A failure in one panel shouldn't block the next one this sheet hosts.
  watch(
    () => [useSheetController.sheets[type].content, useSheetController.sheets[type].open],
    () => error.value = null
  )
</script>

<template>
  <SheetBase 
    :type="type" 
    :starting-height-state="startingHeightState" 
    :position="props.position"
    v-slot="{ transtionName }"
    >
    <SheetContentBase
      v-if="error"
      :type="type"
      :transtionName="transtionName"
    >
      <template v-slot:body>
        <div class="jwx-sheet__content">
          <ErrorBlock :error="errorContent" />
          <button
            @click.prevent="handleRetry"
            class="jwx-button jwx-button--primary"
          >
            <span class="jwx-button__label">Try Again</span>
          </button>
        </div>
      </template>
    </SheetContentBase>
    <Suspense v-else timeout="200">
      <component
        v-if="isSheetOpen"
        :is="useSheetContentComponent(type, components)"
        :transtionName="transtionName"
        :key="retryKey"
      />

      <template #fallback>
        <LoaderCircle />
      </template>
    </Suspense>
  </SheetBase>
</template>